/**
 * The URL prefix every in-app link carries.
 *
 * `/s/<slug>` when a studio is addressed explicitly, empty on the legacy
 * development routes. It is resolved once, from the location the page was
 * opened at, and never changes afterwards — a tenant switch is a full
 * navigation, not a state update.
 *
 * Holding it in a module rather than in context is deliberate: `paths.ts` is a
 * plain module used from loaders, tests and components alike, and threading a
 * hook through all of them would buy nothing for a value that cannot change.
 */
const TENANT_PATH = /^\/s\/([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)(?=\/|$)/

let basePath: string | null = null

/** Extracts `/s/<slug>` from a pathname, or '' when there is none. */
export function basePathFromPathname(pathname: string): string {
  const match = TENANT_PATH.exec(pathname)
  return match === null ? '' : `/s/${String(match[1])}`
}

/** Extracts the slug from a pathname, or null on the legacy routes. */
export function slugFromPathname(pathname: string): string | null {
  const match = TENANT_PATH.exec(pathname)
  return match === null ? null : (match[1] ?? null)
}

export function initBasePath(pathname: string): void {
  basePath = basePathFromPathname(pathname)
}

/**
 * Resolves from the current location on first use.
 *
 * Static imports are evaluated before any statement in the entry module, so a
 * module that builds links at load time would otherwise see an empty prefix
 * and send every tenant to the legacy routes.
 */
export function getBasePath(): string {
  basePath ??=
    typeof window === 'undefined' ? '' : basePathFromPathname(window.location.pathname)
  return basePath
}

/** Prefixes an absolute in-app path with the current studio's base. */
export function withBasePath(path: string): string {
  const base = getBasePath()
  if (base === '') return path
  return path === '/' ? `${base}/` : `${base}${path}`
}

/** Test seam. */
export function resetBasePathForTests(): void {
  basePath = null
}
